import { useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { changeLanguage, SUPPORTED_LANGUAGES } from './i18n';
import type { SupportedLanguage } from './i18n';
import { useTheme } from './theme';
import type { ThemePreference } from './theme';
import { useRatingsVisibility } from './ratingsVisibility';
import { RouteBoundaryWithSuspense } from './routing';
import { useAuth } from './auth/AuthContext';
import { EyeIcon, EyeOffIcon } from './components/icons';

// Moldura comum a todas as rotas: cabeçalho com marca e controles, área de
// conteúdo e rodapé. As telas entram pelo <Outlet />.
export default function Layout() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-full flex-col">
      {/* A barra é escura nos dois temas. Não é capricho: com ela clara, o
          produto inteiro era uma página branca, e a crítica de "monocromático"
          vinha daí antes de vir de qualquer outra tela. O campo à noite é o
          lugar do produto, e a barra é o que o diz sem gastar cor no dado. */}
      <header className="bg-pitch-950 text-chalk-100">
        <div className="mx-auto flex w-full max-w-3xl flex-wrap items-center justify-between gap-x-4 gap-y-3 px-4 py-3 sm:px-6 sm:py-4">
          <Link
            to="/"
            className="rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-turf-400"
          >
            {/* `<span>` e não `<h1>`: a marca se repete em toda rota, e um
                segundo H1 disputa com o título real da página. O da landing é
                "O sorteio que você pode provar" — é ele que deve ser o H1. */}
            <span className="block font-display text-title sm:text-display">
              Balanced<span className="text-turf-400">Squad</span>
            </span>
            <p className="mt-0.5 hidden text-sm text-chalk-400 sm:block">{t('shell.tagline')}</p>
          </Link>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <AccountMenu />
            {/* O olho fica FORA da engrenagem, de propósito: é o único
                controle daqui que se usa no meio de um jogo, com o celular na
                mão e o grupo esperando. Enterrá-lo a um clique de distância
                acabaria com a razão de ele existir. */}
            <HideRatingsToggle />
            <SettingsMenu />
          </div>
        </div>
      </header>

      <VerifyEmailBanner />

      {/* A espera e a queda das rotas sob demanda ficam AQUI dentro, e não em
          volta do app: o cabeçalho e o rodapé já estão na tela e não têm por que
          sumir enquanto o miolo carrega — nem por que desaparecer se ele
          falhar. Uma tela em branco durante a troca de rota se lê como o app
          tendo quebrado. */}
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6 sm:py-12">
        <RouteBoundaryWithSuspense>
          <Outlet />
        </RouteBoundaryWithSuspense>
      </main>

      {/* `dark:text-chalk-400` não é ajuste de gosto: o chalk-500 sozinho media
          3,33:1 sobre o campo à noite, e o rodapé aparece em toda rota do
          produto — era a reprovação de contraste mais repetida do app. */}
      <footer className="border-t border-chalk-200 px-4 py-6 text-xs text-chalk-500 sm:px-6 dark:border-pitch-700 dark:text-chalk-400">
        <nav className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-x-4 gap-y-2">
          <FooterLink to="/termos">{t('legal.terms')}</FooterLink>
          <FooterLink to="/privacidade">{t('legal.privacy')}</FooterLink>
          <FooterLink to="/cookies">{t('legal.cookies')}</FooterLink>
          <FooterLink to="/exclusao-de-dados">{t('legal.deletion')}</FooterLink>
          <FooterLink to="/status">balancedsquad.com</FooterLink>
        </nav>
      </footer>
    </div>
  );
}

function AccountMenu() {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  // Enquanto /api/auth/me não respondeu, não mostra nada: piscar "Entrar" para
  // quem já está autenticado é pior do que esperar um instante.
  if (loading) return null;

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <Link
          to="/entrar"
          className="inline-flex min-h-11 items-center rounded-lg px-3 text-xs font-medium text-chalk-300 hover:bg-pitch-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-turf-400"
        >
          {t('auth.signIn')}
        </Link>
        <Link
          to="/criar-conta"
          className="inline-flex min-h-11 items-center rounded-lg bg-turf-400 px-3 text-xs font-semibold text-pitch-950 hover:bg-turf-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-turf-400"
        >
          {t('auth.signUp')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* O email vira o acesso à conta: é onde a pessoa procura, e evita mais
          um item no cabeçalho. */}
      <Link
        to="/conta"
        className="max-w-[9rem] truncate text-xs text-chalk-400 underline underline-offset-2 hover:text-turf-300 sm:max-w-[12rem]"
        title={user.email}
      >
        {user.email}
      </Link>
      <button
        type="button"
        onClick={() => void logout().then(() => navigate('/'))}
        className="inline-flex min-h-11 items-center rounded-lg border border-pitch-600 px-3 text-xs font-medium hover:bg-pitch-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-turf-400"
      >
        {t('auth.signOut')}
      </button>
    </div>
  );
}

// Lembrete para quem criou conta e não confirmou o email. Some sozinho quando
// a confirmação acontece.
function VerifyEmailBanner() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [sent, setSent] = useState(false);

  if (!user || user.emailVerified) return null;

  async function resend() {
    await fetch('/api/auth/resend-verification', { method: 'POST' }).catch(() => undefined);
    setSent(true);
    await refresh();
  }

  return (
    <div className="border-b border-chalk-300 bg-chalk-100 px-4 py-2.5 text-sm text-pitch-900 sm:px-6 dark:border-pitch-600 dark:bg-pitch-800 dark:text-chalk-200">
      <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
        <span>{sent ? t('auth.verifyResent') : t('auth.verifyBanner')}</span>
        {!sent && (
          <button
            type="button"
            onClick={() => void resend()}
            className="rounded-sm font-semibold underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-focus"
          >
            {t('auth.verifyResend')}
          </button>
        )}
      </div>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      // O alvo tem 44px de altura ainda que o texto tenha 16: são links de
      // documento jurídico, tocados com o polegar num rodapé apertado, e a
      // altura da linha não é a altura do alvo.
      className="inline-flex min-h-11 items-center rounded-sm px-1 hover:text-chalk-600 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-focus dark:hover:text-chalk-200"
    >
      {children}
    </Link>
  );
}

function ThemeSelect() {
  const { t } = useTranslation();
  const { preference, setPreference } = useTheme();

  const options: { value: ThemePreference; label: string }[] = [
    { value: 'system', label: t('shell.themeSystem') },
    { value: 'light', label: t('shell.themeLight') },
    { value: 'dark', label: t('shell.themeDark') },
  ];

  return (
    <div
      role="radiogroup"
      aria-label={t('shell.appearance')}
      className="flex rounded-lg border border-pitch-600 p-0.5"
    >
      {options.map((option) => {
        const active = preference === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => setPreference(option.value)}
            className={[
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-turf-400',
              active ? 'bg-chalk-100 text-pitch-950' : 'text-chalk-400 hover:bg-pitch-800',
            ].join(' ')}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Engrenagem com o que se ajusta uma vez e não se mexe mais.
 *
 * O cabeçalho tinha quatro controles soltos e competindo entre si. Tema e
 * idioma são decisões de uma vez na vida — não merecem espaço permanente ao
 * lado do nome do produto.
 */
function SettingsMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        title={t('shell.settings')}
        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-pitch-600 bg-pitch-900 text-base font-medium text-chalk-100 transition-colors hover:bg-pitch-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-turf-400"
      >
        <span aria-hidden>⚙</span>
        <span className="sr-only">{t('shell.settings')}</span>
      </button>

      {open && (
        <>
          {/* Camada de fechar ao clicar fora. Um menu que só fecha pelo próprio
              botão vira um menu que fica aberto. */}
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />

          <div className="absolute right-0 z-20 mt-2 flex w-60 flex-col gap-4 rounded-xl border border-pitch-700 bg-pitch-900 p-4 shadow-lg">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-chalk-400">{t('shell.appearance')}</span>
              <ThemeSelect />
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-chalk-400">{t('shell.language')}</span>
              <LanguageSelect />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Esconde os níveis na tela de quem já pode vê-los.
 *
 * Mora no cabeçalho, e não dentro da pelada, porque o momento de usá-lo é
 * antes de virar o celular para alguém — não depois de navegar até a aba
 * certa. É o mesmo lugar que ele ocupa no mobile.
 */
function HideRatingsToggle() {
  const { t } = useTranslation();
  const { hidden, toggle } = useRatingsVisibility();

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={hidden}
      title={hidden ? t('shell.showRatings') : t('shell.hideRatings')}
      // 44px nos dois eixos. Este é, por decisão registrada logo acima, o único
      // controle do cabeçalho usado NO MEIO do jogo, com o celular na mão e o
      // grupo esperando — e era o menor alvo da barra, com 33×29.
      className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-pitch-600 bg-pitch-900 text-base font-medium text-chalk-100 transition-colors hover:bg-pitch-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-turf-400"
    >
      {/* Olho e olho cortado, em SVG: é a convenção de campo de senha, e o par
          de emoji anterior (🙈 / 👁) mudava de desenho e de cor conforme o
          sistema operacional. */}
      {hidden ? <EyeOffIcon /> : <EyeIcon />}
      <span className="sr-only">{hidden ? t('shell.showRatings') : t('shell.hideRatings')}</span>
    </button>
  );
}

function LanguageSelect() {
  const { t, i18n } = useTranslation();

  // ⚠️ A escolha aparece na hora, e não quando o idioma termina de chegar.
  //
  // Desde que cada idioma virou um arquivo próprio, trocar envolve uma ida à
  // rede. O `<select>` é controlado por `i18n.resolvedLanguage`, que só muda no
  // fim — então numa rede ruim escolher "Español" fazia o campo VOLTAR sozinho
  // para o idioma antigo e ficar assim por segundos. É o mesmo defeito do
  // seletor de papéis de 30/07, e a mesma correção: o que a pessoa escolheu
  // vale na tela, e o servidor (aqui, a rede) confirma depois.
  const [pending, setPending] = useState<SupportedLanguage | null>(null);

  async function pick(lang: SupportedLanguage) {
    setPending(lang);
    try {
      await changeLanguage(lang);
    } finally {
      // Solta o controle de volta para o i18next: se a busca falhou, o campo
      // volta a mostrar o idioma que de fato está valendo, em vez de mentir
      // para sempre sobre uma troca que não aconteceu.
      setPending(null);
    }
  }

  return (
    <label className="flex items-center gap-2">
      <span className="sr-only">{t('shell.language')}</span>
      <select
        value={pending ?? i18n.resolvedLanguage}
        onChange={(e) => void pick(e.target.value as SupportedLanguage)}
        className="w-full rounded-lg border border-pitch-600 bg-pitch-950 px-2.5 py-1.5 text-xs font-medium text-chalk-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-turf-400"
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.flag} {lang.label}
          </option>
        ))}
      </select>
    </label>
  );
}
